import { useMemo } from 'react';

interface EnrollingStudent {
  id: string;
  firstName: string;
  lastName: string;
  type: 'returning' | 'new';
}

interface EnrollingStudentsInput {
  returningStudents?: any[];
  persistedNewStudents?: any[];
  unpersistedNewStudents?: any[];
  persistedNewStudentsToRemove?: string[];
}

/**
 * Normalizes the 4-source student list pattern used across reenrollment steps.
 *
 * Sources:
 * - returningStudents (filtered to isReturning !== false)
 * - persistedNewStudents (minus those in persistedNewStudentsToRemove)
 * - unpersistedNewStudents
 */
export function useEnrollingStudents(formData: EnrollingStudentsInput | null | undefined): EnrollingStudent[] {
  return useMemo(() => {
    if (!formData) return [];

    const returning = (formData.returningStudents || [])
      .filter((s: any) => s.isReturning !== false)
      .map((s: any) => ({
        id: s.studentEnrollmentId,
        firstName: s.firstName,
        lastName: s.lastName,
        type: 'returning' as const,
      }));

    const toRemove = formData.persistedNewStudentsToRemove || [];

    const persistedNew = (formData.persistedNewStudents || [])
      .filter((s: any) => !toRemove.includes(s.studentEnrollmentId))
      .filter((s: any) => s.firstName || s.lastName)
      .map((s: any) => ({
        id: s.studentEnrollmentId,
        firstName: s.firstName,
        lastName: s.lastName,
        type: 'new' as const,
      }));

    const unpersistedNew = (formData.unpersistedNewStudents || [])
      .filter((s: any) => s.firstName || s.lastName)
      .map((s: any) => ({
        id: s.tempId,
        firstName: s.firstName,
        lastName: s.lastName,
        type: 'new' as const,
      }));

    return [...returning, ...persistedNew, ...unpersistedNew];
  }, [
    formData?.returningStudents,
    formData?.persistedNewStudents,
    formData?.unpersistedNewStudents,
    formData?.persistedNewStudentsToRemove,
  ]);
}
